import {SC_ServiceFile} from "./files/SC_ServiceFile";
import {SC_Originator} from "./enums/SC_Originator";
import {SC_Location} from "./SC_Location";
import {SC_BasicNote} from "./notes/SC_BasicNote";
import {applyPAT} from "../LinkUtils";
import {SC_BasicNotefromAPI} from "./notes/NoteUtils";
import {SC_WorkorderReminder} from "./SC_WorkorderReminder";
import {SC_Client} from "./SC_Client";
import {SC_LocationFilter} from "./filters/SC_LocationFilter";
import {SC_PaginationState} from "./filters/SC_PaginationState";
import {SC_WorkorderFilter} from "./filters/SC_WorkorderFilter";
import {SC_User} from "./SC_User";
import {SC_ServiceInstance} from "./SC_ServiceInstance";
import {SC_CrewBase} from "./crews/SC_CrewBase";
import {SC_ApiTimezone} from "./enums/SC_ApiTimezone";
import {SC_ForceType} from "./enums/SC_ForceType";
import {SC_Department} from "./enums/SC_Department";
import FontAwesomeButton from "../../components/cards/FontAwesomeButton";
import {faArrowUpRightFromSquare} from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import {SC_QCStatuses} from "./enums/SC_QCStatuses";
import {SC_ImageStatusesClone} from "./enums/SC_ImageStatusesClone";
import {SC_WorkorderStatus} from "./enums/SC_WorkorderStatus";
import {SC_ConfirmationLevelEnum} from "./enums/SC_ConfirmationLevel";
import {SC_QCReportStatuses} from "./enums/SC_QCReportStatuses";
import {SC_GenericConfirmation} from "./SC_GenericConfirmation";
import {SC_LocationService} from "./SC_LocationService";

export class SC_WorkorderObject {
    /**
     * Input property name constants for fromAPI
     */
    static inputKeys = {
        internalID: 'internalID',
        externalID: 'externalID',
        domain: 'domain',
        originator: 'originator',
        description: 'description',
        shortDescription: 'shortDescription',
        serviceTinyDescription: 'serviceTinyDescription',
        priority: 'priority',
        locationID: 'locationID',
        clientAbbr: 'clientAbbr',
        defaultCrewID: 'defaultCrewID',
        confirmed: 'confirmed',
        status: 'status',
        signatureCollected: 'signatureCollected',
        report: 'report',
        beforeImages: 'beforeImages',
        afterImages: 'afterImages',
        crewImages: 'crewImages',
        flagged: 'flagged',
        billed: 'billed',
        paid: 'paid',
        paid_cents: 'paid_amt',
        windowOpen: 'windowOpen',
        windowClose: 'windowClose',
        internalClient: 'internalClient',
        responsiveness: 'responsiveness',
        quality: 'quality',
        comments: 'comments',
        lastIssue: 'lastIssue',
        totalIssues: 'totalIssues',
        externalDisplayID: 'externalDisplayID',
        lastInternalNote: 'lastInternalNote',
        lastExternalNote: 'lastExternalNote',
        reminders: 'reminders',
        monies: 'mons',
        external_status_string: 'stats',
        external_status_code: 'statc',
        external_modified_time: 'lamod',
        confirmations: 'confirmations',
        followUpUsers: 'fuu',
        followUpDepartments: 'fud',
    } as const;

    internalID: number | null;
    externalID: string;
    domain: string;
    originator: SC_Originator | null;
    description: string | null;
    shortDescription: string | null;
    serviceTinyDescription: string | null;
    priority: number | null;
    locationID: number | null;
    clientAbbr: string;
    defaultCrewID: number;
    confirmed: boolean;
    status: SC_WorkorderStatus | null;
    signatureCollected: boolean;
    report: SC_ServiceFile | null;
    beforeImages: SC_ServiceFile[] | null;
    afterImages: SC_ServiceFile[] | null;
    crewImages: SC_ServiceFile[] | null;
    flagged: boolean;
    billed: boolean;
    paid: boolean;
    paid_cents: number | null;
    windowOpen: string | null;
    windowOpenDate: Date | null;
    windowClose: string | null;
    windowCloseDate: Date | null;
    location: SC_Location | null;
    defaultCrew: any | null;
    internalClient: SC_Client | null;
    responsiveness: number | null;
    quality: number | null;
    comments: string | null;
    lastIssue: number | null;
    totalIssues: number | null;
    externalDisplayID: string | null;
    lastInternalNote: SC_BasicNote | null;
    lastExternalNote: SC_BasicNote | null;
    reminders: SC_WorkorderReminder[] | null;
    services: SC_ServiceInstance[] | null;
    budget_cents: number | null;
    external_status_string: string | null;
    external_status_code: string | null;
    external_modified_time: Date | null;
    confirmations: SC_GenericConfirmation[] | null;
    followUpUsers: number[] | null;
    followUpUsersHydrated: SC_User[] | null;
    followUpDepartments: SC_Department[] | null;

    constructor({
        internalID = null,
        externalID = "",
        domain = "",
        originator = null,
        description = null,
        shortDescription = null,
        serviceTinyDescription = null,
        priority = null,
        locationID = null,
        clientAbbr = "",
        defaultCrewID = -1,
        confirmed = false,
        status = null,
        signatureCollected = false,
        report = null,
        beforeImages = null,
        afterImages = null,
        crewImages = null,
        flagged = false,
        billed = false,
        paid = false,
        paid_cents = null,
        windowOpen = null,
        windowClose = null,
        internalClient = null,
        responsiveness = null,
        quality = null,
        comments = null,
        totalIssues = null,
        lastIssue = null,
        externalDisplayID = null,
        lastInternalNote = null,
        lastExternalNote = null,
        reminders = null,
        monies = null,
        external_status_string = null,
        external_status_code = null,
        external_modified_time = null,
        confirmations = null,
        followUpUsers = null,
        followUpUsersHydrated = null,
        followUpDepartments = null,
    } = {}) {
        this.internalID = internalID;
        this.externalID = externalID;
        this.domain = domain;
        this.originator = originator;
        this.description = description;
        this.shortDescription = shortDescription;
        this.serviceTinyDescription = serviceTinyDescription;
        this.priority = priority;
        this.locationID = locationID;
        this.clientAbbr = clientAbbr;
        this.defaultCrewID = defaultCrewID;
        this.confirmed = confirmed;
        this.status = status;
        this.signatureCollected = signatureCollected;
        this.report = report;
        this.beforeImages = beforeImages;
        this.afterImages = afterImages;
        this.crewImages = crewImages;
        this.flagged = flagged;
        this.paid = paid;
        this.paid_cents = paid_cents;
        this.billed = billed;
        this.windowOpen = windowOpen;
        this.windowOpenDate = windowOpen ? new Date(windowOpen) : null;
        this.windowClose = windowClose;
        this.windowCloseDate = windowClose ? new Date(windowClose) : null;
        this.location = null;
        this.defaultCrew = null;
        this.internalClient = internalClient;
        this.responsiveness = responsiveness;
        this.quality = quality;
        this.comments = comments;
        this.lastIssue = lastIssue;
        this.totalIssues = totalIssues;
        this.externalDisplayID = externalDisplayID;
        this.lastInternalNote = lastInternalNote;
        this.lastExternalNote = lastExternalNote;
        this.reminders = reminders;
        this.budget_cents = monies;
        this.external_status_string = external_status_string;
        this.external_status_code = external_status_code;
        this.external_modified_time = external_modified_time ? new Date(external_modified_time) : null;
        this.confirmations = confirmations;
        this.followUpUsers = followUpUsers;
        this.followUpUsersHydrated = followUpUsersHydrated;
        this.followUpDepartments = followUpDepartments;
    }

    static fromAPI(
        jsonin: Record<string, any>,
        locsCache?: SC_Location[],
        usersCache?: SC_User[],
        servicesCache?: SC_ServiceInstance[],
    ): SC_WorkorderObject {
        const out = new SC_WorkorderObject({
            internalID: jsonin[SC_WorkorderObject.inputKeys.internalID] ?? null,
            externalID: jsonin[SC_WorkorderObject.inputKeys.externalID] ?? null,
            domain: jsonin[SC_WorkorderObject.inputKeys.domain] ?? null,
            originator: SC_Originator.fromAPI(jsonin[SC_WorkorderObject.inputKeys.originator]),
            description: jsonin[SC_WorkorderObject.inputKeys.description] ?? null,
            shortDescription: jsonin[SC_WorkorderObject.inputKeys.shortDescription] ?? null,
            serviceTinyDescription: jsonin[SC_WorkorderObject.inputKeys.serviceTinyDescription] ?? null,
            priority: jsonin[SC_WorkorderObject.inputKeys.priority] ?? null,
            locationID: jsonin[SC_WorkorderObject.inputKeys.locationID] ?? null,
            clientAbbr: jsonin[SC_WorkorderObject.inputKeys.clientAbbr] ?? "",
            defaultCrewID: jsonin[SC_WorkorderObject.inputKeys.defaultCrewID] ?? -1,
            confirmed: jsonin[SC_WorkorderObject.inputKeys.confirmed] ?? null,
            status: jsonin[SC_WorkorderObject.inputKeys.status] ? SC_WorkorderStatus.fromAPI(jsonin[SC_WorkorderObject.inputKeys.status]) : null,
            signatureCollected: jsonin[SC_WorkorderObject.inputKeys.signatureCollected] ?? null,
            report: jsonin[SC_WorkorderObject.inputKeys.report] ? SC_ServiceFile.fromAPI(jsonin[SC_WorkorderObject.inputKeys.report]) : null,
            beforeImages: jsonin[SC_WorkorderObject.inputKeys.beforeImages] ? SC_ServiceFile.fromAPIArray(jsonin[SC_WorkorderObject.inputKeys.beforeImages], usersCache, servicesCache) : null,
            afterImages: jsonin[SC_WorkorderObject.inputKeys.afterImages] ? SC_ServiceFile.fromAPIArray(jsonin[SC_WorkorderObject.inputKeys.afterImages], usersCache, servicesCache) : null,
            crewImages: jsonin[SC_WorkorderObject.inputKeys.crewImages] ? SC_ServiceFile.fromAPIArray(jsonin[SC_WorkorderObject.inputKeys.crewImages], usersCache, servicesCache) : null,
            flagged: jsonin[SC_WorkorderObject.inputKeys.flagged] ?? null,
            billed: jsonin[SC_WorkorderObject.inputKeys.billed] ?? null,
            paid: jsonin[SC_WorkorderObject.inputKeys.paid] ?? null,
            paid_cents: jsonin[SC_WorkorderObject.inputKeys.paid_cents] ?? null,
            windowOpen: jsonin[SC_WorkorderObject.inputKeys.windowOpen] ?? null,
            windowClose: jsonin[SC_WorkorderObject.inputKeys.windowClose] ?? null,
            internalClient: jsonin[SC_WorkorderObject.inputKeys.internalClient] ?? null,
            responsiveness: jsonin[SC_WorkorderObject.inputKeys.responsiveness] ?? null,
            quality: jsonin[SC_WorkorderObject.inputKeys.quality] ?? null,
            comments: jsonin[SC_WorkorderObject.inputKeys.comments] ?? null,
            lastIssue: jsonin[SC_WorkorderObject.inputKeys.lastIssue] ?? null,
            totalIssues: jsonin[SC_WorkorderObject.inputKeys.totalIssues] ?? null,
            externalDisplayID: jsonin[SC_WorkorderObject.inputKeys.externalDisplayID] ?? null,
            lastInternalNote: jsonin[SC_WorkorderObject.inputKeys.lastInternalNote] ? SC_BasicNotefromAPI(jsonin[SC_WorkorderObject.inputKeys.lastInternalNote]) : null,
            lastExternalNote: jsonin[SC_WorkorderObject.inputKeys.lastExternalNote] ? SC_BasicNotefromAPI(jsonin[SC_WorkorderObject.inputKeys.lastExternalNote]) : null,
            reminders: jsonin[SC_WorkorderObject.inputKeys.reminders]?.map((r: Record<string, any>) => SC_WorkorderReminder.fromAPI(r, usersCache)) ?? null,
            monies: jsonin[SC_WorkorderObject.inputKeys.monies] ?? null,
            external_status_string: jsonin[SC_WorkorderObject.inputKeys.external_status_string] ?? null,
            external_status_code: jsonin[SC_WorkorderObject.inputKeys.external_status_code] ?? null,
            external_modified_time: jsonin[SC_WorkorderObject.inputKeys.external_modified_time] ?? null,
            confirmations: jsonin[SC_WorkorderObject.inputKeys.confirmations]?.map((c: Record<string, any>) => SC_GenericConfirmation.fromAPI(c, usersCache)) ?? null,
            followUpUsers: jsonin[SC_WorkorderObject.inputKeys.followUpUsers] ?? null,
            followUpUsersHydrated: jsonin[SC_WorkorderObject.inputKeys.followUpUsers]?.map((id: number) => SC_User.fromAPI({insiteID: id})) ?? null,
            followUpDepartments: jsonin[SC_WorkorderObject.inputKeys.followUpDepartments]?.map((d: any) => SC_Department.fromAPI(d)) ?? null,
        });

        if (locsCache)
            out.scavengeLoc(locsCache);

        if (usersCache)
            out.scavengeUsers(usersCache);

        if (servicesCache)
            out.scavengeServices(servicesCache);

        return out;
    }

    toAPI(): Record<string, unknown> {
        return {
            internalID: this.internalID,
            externalID: this.externalID,
            domain: this.domain,
            originator: this.originator.toAPI(),
            description: this.description,
            shortDescription: this.shortDescription,
            [SC_WorkorderObject.inputKeys.serviceTinyDescription]: this.serviceTinyDescription,
            priority: this.priority,
            locationID: this.locationID,
            clientAbbr: this.clientAbbr,
            defaultCrewID: this.defaultCrewID,
            confirmed: this.confirmed,
            status: this.status,
            signatureCollected: this.signatureCollected,
            flagged: this.flagged,
            billed: this.billed,
            paid: this.paid,
            paid_cents: this.paid_cents,
            windowOpen: this.windowOpen,
            windowClose: this.windowClose,
            internalClient: this.internalClient,
            responsiveness: this.responsiveness,
            quality: this.quality,
            comments: this.comments,
            lastIssue: this.lastIssue,
            totalIssues: this.totalIssues,
            externalDisplayID: this.externalDisplayID,
            monies: this.budget_cents,
            followUpUsers: this.followUpUsers,
            [SC_WorkorderObject.inputKeys.followUpDepartments]: this.followUpDepartments?.map(d => d.toAPI()) ?? null,
        };
    }

    clone(): SC_WorkorderObject {
        const cloned = new SC_WorkorderObject();

        cloned.internalID = this.internalID;
        cloned.externalID = this.externalID;
        cloned.originator = this.originator.clone();
        cloned.description = this.description;
        cloned.shortDescription = this.shortDescription;
        cloned.serviceTinyDescription = this.serviceTinyDescription;
        cloned.priority = this.priority;
        cloned.locationID = this.locationID;
        cloned.clientAbbr = this.clientAbbr;
        cloned.defaultCrewID = this.defaultCrewID;
        cloned.confirmed = this.confirmed;
        cloned.status = this.status;
        cloned.signatureCollected = this.signatureCollected;
        cloned.report = this.report ? this.report.clone() : null;
        cloned.flagged = this.flagged;
        cloned.billed = this.billed;
        cloned.paid = this.paid;
        cloned.paid_cents = this.paid_cents;
        cloned.windowOpen = this.windowOpen;
        cloned.windowClose = this.windowClose;
        cloned.responsiveness = this.responsiveness;
        cloned.quality = this.quality;
        cloned.comments = this.comments;
        cloned.lastIssue = this.lastIssue;
        cloned.totalIssues = this.totalIssues;

        cloned.confirmations = this.confirmations?.map(c => c.clone());
        cloned.followUpUsers = [...this.followUpUsers ?? []];
        cloned.followUpUsersHydrated = this.followUpUsersHydrated?.map(u => u.clone()) ?? null;
        cloned.followUpDepartments = this.followUpDepartments;

        if (this.location) {
            cloned.location = this.location.clone();
        }

        cloned.internalClient = this.internalClient;
        cloned.externalDisplayID = this.externalDisplayID;

        cloned.windowOpenDate = this.windowOpenDate;
        cloned.windowCloseDate = this.windowCloseDate;

        cloned.reminders = this.reminders;

        cloned.budget_cents = this.budget_cents;

        cloned.external_status_string = this.external_status_string;
        cloned.external_status_code = this.external_status_code;

        cloned.external_modified_time = this.external_modified_time;

        return cloned;
    }

    merge(other: SC_WorkorderObject): SC_WorkorderObject {
        if (other.internalID !== null) this.internalID = other.internalID;
        if (other.externalID !== "") this.externalID = other.externalID;
        if (other.description !== null) this.description = other.description;
        if (other.shortDescription !== null) this.shortDescription = other.shortDescription;
        if (other.serviceTinyDescription !== null) this.serviceTinyDescription = other.serviceTinyDescription;
        if (other.priority !== null) this.priority = other.priority;
        if (other.locationID !== null) this.locationID = other.locationID;
        if (other.clientAbbr !== "") this.clientAbbr = other.clientAbbr;
        if (other.defaultCrewID !== -1) this.defaultCrewID = other.defaultCrewID;
        if (other.confirmed !== false) this.confirmed = other.confirmed;
        if (other.status !== null) this.status = other.status;
        if (other.signatureCollected !== false) this.signatureCollected = other.signatureCollected;
        if (other.report !== null) this.report = other.report;
        if (other.flagged !== false) this.flagged = other.flagged;
        if (other.billed !== false) this.billed = other.billed;
        if (other.paid !== false) this.paid = other.paid;
        if (other.paid_cents !== null) this.paid_cents = other.paid_cents;
        if (other.windowOpen !== null) this.windowOpen = other.windowOpen;
        if (other.windowOpenDate !== null) this.windowOpenDate = other.windowOpenDate;
        if (other.windowClose !== null) this.windowClose = other.windowClose;
        if (other.windowCloseDate !== null) this.windowCloseDate = other.windowCloseDate;
        if (other.responsiveness !== null) this.responsiveness = other.responsiveness;
        if (other.quality !== null) this.quality = other.quality;
        if (other.comments !== null) this.comments = other.comments;
        if (other.lastIssue !== null) this.lastIssue = other.lastIssue;
        if (other.totalIssues !== null) this.totalIssues = other.totalIssues;
        if (other.externalDisplayID !== null) this.externalDisplayID = other.externalDisplayID;
        if (other.budget_cents !== null) this.budget_cents = other.budget_cents;
        if (other.external_status_string !== null) this.external_status_string = other.external_status_string;
        if (other.external_status_code !== null) this.external_status_code = other.external_status_code;
        if (other.external_modified_time !== null) this.external_modified_time = other.external_modified_time;

        if (other.confirmations !== null) this.confirmations = other.confirmations;
        if (other.followUpUsers !== null) this.followUpUsers = [...other.followUpUsers];
        if (other.followUpUsersHydrated !== null) this.followUpUsersHydrated = other.followUpUsersHydrated;
        if (other.followUpDepartments !== null) this.followUpDepartments = other.followUpDepartments;

        if (other.location !== null && this.location === null) {
            this.location = other.location.clone();
        } else if (other.location !== null && this.location !== null) {
            this.location.merge(other.location);
        }

        if (other.internalClient !== null) {
            this.internalClient = other.internalClient;
        }

        if (other.reminders !== null) {
            this.reminders = other.reminders;
        }

        return this;
    }

    scavengeLoc(locarr: SC_Location[]): void {
        if (locarr) {
            const location = locarr.find(it => it.internalID === this.locationID);
            if (location) {
                this.location = location;

                let tz = SC_ApiTimezone.fromString(location.address.timezone_id);

                this.beforeImages?.forEach(img => img.rehydrateDates(tz));
                this.afterImages?.forEach(img => img.rehydrateDates(tz));
                this.crewImages?.forEach(img => img.rehydrateDates(tz));
            }
        }
    }

    scavengeServices(servarr: SC_ServiceInstance[]): void {
        this.services = servarr.filter(it => it.parentID === this.internalID);
    }

    scavengeUsers(usarr: SC_User[]): void {
        if (this.reminders) {
            this.reminders.forEach(reminder => reminder.scavengeUser(usarr));
        }

        if (this.afterImages)
            this.afterImages.forEach(image => image.scavengeUsers(usarr));

        if (this.beforeImages)
            this.beforeImages.forEach(image => image.scavengeUsers(usarr));

        if (this.crewImages)
            this.crewImages.forEach(image => image.scavengeUsers(usarr));

        if (this.confirmations)
            this.confirmations.forEach(conf => conf.scavengeUsers(usarr));
    }

    qcStatus(): SC_QCStatuses {
        if (this.report) {
            if (this.report.uploadinged)
                return SC_QCStatuses.fromAPI(SC_QCStatuses.COMPLETE);
            if (this.report.reviewed)
                return SC_QCStatuses.fromAPI(SC_QCStatuses.REQUIRES_UPLOAD);
            return SC_QCStatuses.fromAPI(SC_QCStatuses.REQUIRES_REVIEW);
        }
        if (this.totalIssues)
            return SC_QCStatuses.fromAPI(SC_QCStatuses.AWAITING);
        return SC_QCStatuses.fromAPI(SC_QCStatuses.NEEDS_ISSUING);
    }

    qcStatuss(): SC_QCReportStatuses {
        if (this.report) {
            if (this.report.uploadinged || (this.report.reviewed && !this.originator.is_external()))
                if (this.quality <= 2 || this.responsiveness <= 2)
                    return SC_QCReportStatuses.fromAPI(SC_QCReportStatuses.COMPLIANCE_ERROR);
                else
                    return SC_QCReportStatuses.fromAPI(SC_QCReportStatuses.COMPLETE);
            return SC_QCReportStatuses.fromAPI(SC_QCReportStatuses.FINAL_REVIEW);
        }
        if (this.totalIssues)
            return SC_QCReportStatuses.fromAPI(SC_QCReportStatuses.AWAITING_CLIENT);
        if (this.location.required_ivr && this.services.some(it => it.forceType.toAPI() && it.forceType.isWorky))
            return SC_QCReportStatuses.fromAPI(SC_QCReportStatuses.IVR_NO_SHOW);
        return SC_QCReportStatuses.fromAPI(SC_QCReportStatuses.NOT_ISSUED);
    }

    pictureStatus(): SC_ImageStatusesClone {
        if (!this.services.length)
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.ANY);

        let filesDict = this.allImages.reduce((out, curr) => {
            if (!(curr.service_id in out))
                out[curr.service_id] = [];

            out[curr.service_id].push(curr);

            return out;
        }, {});

        return this.services.reduce((out, curr) => {
            if (!curr.completed && !curr.etaMissed) {
                if (out.is(SC_ImageStatusesClone.FINAL_REVIEW))
                    return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.DAILY_REVIEW);
                if (out.is(SC_ImageStatusesClone.COMPLETE))
                    return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.PENDING_FINAL_REVIEW);

                return out;
            }

            if (!curr.forceType.isWorky)
                return out;

            let newStatus = curr.picturesStatus(filesDict[curr.internalID] ?? []);

            if (newStatus.toAPI() < SC_ImageStatusesClone.COMPLETE && newStatus.toAPI() < out.toAPI())
                return newStatus;
            else
                return out;
        }, SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.COMPLETE));
    }

    get allImages(): SC_ServiceFile[] {
        return (this.crewImages ?? []).concat((this.beforeImages ?? []).concat(this.afterImages ?? []));
    }

    get reportedImages(): SC_ServiceFile[] {
        return this.allImages.filter(it => it.reported);
    }

    static getWorkorders(
        filter: SC_WorkorderFilter,
        lofilter: SC_LocationFilter,
        pagination: SC_PaginationState,
        doRequest: (formData: FormData, action: string, doLoading: boolean) => Promise<any>,
        clientCache?: SC_Client[],
        usersCache?: SC_User[],
        crewsCache?: SC_CrewBase[],
    ): Promise<{pages: number, workorders: SC_WorkorderObject[], locations: SC_Location[]}> {
        return new Promise(async (resolve, reject) => {
            let formData = new FormData();

            filter.applyToFormData(formData);
            lofilter.applyToFormData(formData);

            pagination.applyToFormData(formData);

            let back = await doRequest(formData, "list_workorders", true);

            let locationsCache = back.reason.locations.map((wo: Record<string, unknown>) => SC_Location.fromAPI(wo, clientCache));

            let servs = back.reason.services.map(qc => SC_ServiceInstance.fromAPI(qc, crewsCache));

            let out: {
                pages: number;
                workorders: SC_WorkorderObject[];
                locations: SC_Location[];
            } = { pages: back.reason.total, locations: locationsCache, workorders: back.reason.workorders.map((wo) => SC_WorkorderObject.fromAPI(wo, locationsCache, usersCache, servs)) };

            resolve(out);
        });
    }

    get externalURL() {
        switch (this.originator.toAPI()) {
            case SC_Originator.SERVICE_CHANNEL:
                return `https://www.servicechannel.com/sc/wo/Workorders/index?id=${this.externalID}`;
            case SC_Originator.FM_PILOT:
                return `https://www.fmpilot2.com/WorkOrder/Overview.v2.aspx?WOID=${this.externalID}&UserAction=view&menuIndex=&FilterContext=Default&ClientID=${this._domainID}`;
                // return `https://azstaging.fmpilot2.com/WorkOrder/Overview.v2.aspx?WOID=${this.externalID}&UserAction=view&menuIndex=&FilterContext=Default&ClientID=66`;
            default:
                return '';
        }
    }

    get _domainID() {
        if (!this.domain)
            return "66";

        if (this.domain === "IFM")
            return 66;
        if (this.domain === "ZAYO")
            return 370;

        return "66";
    }

    get internalURL(): string {
        return applyPAT(`/neo-view-workorder/?woid=${this.internalID}`);
    }

    toggleFlag(doRequest: (arg0: FormData, arg1: string) => Promise<any>, replaceState = (v: any, internalID: number) => {}) {
        let formData = new FormData();

        formData.append("woid", this.internalID.toString());
        formData.append("new_status", String(!this.flagged));

        doRequest(formData, "flag_workorderr").then((v) => { replaceState(v, this.internalID) });
    }

    toggleBilled(doRequest: (arg0: FormData, arg1: string) => Promise<any>, replaceState = (v: any, internalID: number) => {}) {
        let formData = new FormData();

        formData.append("woid", this.internalID.toString());
        formData.append("new_status", String(!this.billed));

        doRequest(formData, "bill_workorder").then((v) => { replaceState(v, this.internalID) });
    }

    get lastNote() : SC_BasicNote | null {
        if (this.lastInternalNote && this.lastExternalNote) {
            if (this.lastInternalNote.noteCreatedTime > this.lastExternalNote.noteCreatedTime)
                return this.lastInternalNote;
            else
                return this.lastExternalNote;
        } else if (this.lastInternalNote)
            return this.lastInternalNote;
        else if (this.lastExternalNote)
            return this.lastExternalNote;

        return null;
    }

    get humanExternalID(): string {
        return this.externalDisplayID ?? this.externalID;
    }

    get budget_dollars(): number | null {
        if (this.budget_cents === null) return null; return this.budget_cents / 100;
    }

    get paid_dollars(): number | null {
        if (this.paid_cents === null) return null; return this.paid_cents / 100;
    }

    canCheckIn() : boolean {
        return !this.services.some(serv => serv.completed && serv.forceType.is(SC_ForceType.NULL) && serv.actualEnd && ((new Date()).getTime() - serv.actualEnd.getTime() < 1000 * 60 * 15))
    }

    externalInlineLink() {
        const href = this.externalURL;

        if (href)
            return FontAwesomeButton({
                icon: faArrowUpRightFromSquare,
                buttonType: null,
                href: href
            });

        return null;
    }

    get firstService(): SC_ServiceInstance | undefined {
        if (!this.services.length)
            return undefined;
        return this.services.reduce((out, curr) => out.serviceDate > curr.serviceDate ? curr : out);
    }

    get lastService(): SC_ServiceInstance | undefined {
        if (!this.services.length)
            return undefined;
        return this.services.reduce((out, curr) => out.serviceDate < curr.serviceDate ? curr : out);
    }

    get defaultLocationService() : SC_LocationService | undefined {
        const littleD = this.description.toLowerCase();

        return this.location.services.find(s => littleD.includes(s.serviceSearchString.toLowerCase()))
    }
}
